/**
 * Optional candidate detail fields — notice period, current / expected CTC, visa sponsorship.
 *
 * NOT every hiring team asks for these, and CTC is personal financial data, so nothing here is
 * collected unless a team switches the field on:
 *   - the ORG DEFAULT (organizations.candidate_fields, migration 0090) applies to every job;
 *   - a JOB OVERRIDE (requisitions.config.candidateFields) replaces it for that one job — an
 *     empty array is a real override meaning "none for this job", `undefined` means "inherit".
 *
 * Values come from two places and are kept APART, never merged:
 *   - what the CANDIDATE reported on their "complete your application" page
 *     (candidacies.submitted_profile, 0087) — self-reported, unverified;
 *   - what the RECRUITER recorded (candidacies.recruiter_details, 0090) — e.g. after a call.
 * Neither is ever scored, ranked or gated on: they are context for a person, nothing more.
 */
import { z } from "zod";
export const CANDIDATE_FIELD_KEYS = ["noticePeriodDays", "currentCtc", "expectedCtc", "visaNeeded"];
export const CANDIDATE_FIELD_META = {
    noticePeriodDays: { label: "Notice period", kind: "days", placeholder: "Days, 0 if immediate" },
    currentCtc: { label: "Current CTC", kind: "text", placeholder: "e.g. ₹12 LPA" },
    expectedCtc: { label: "Expected CTC", kind: "text", placeholder: "e.g. ₹16 LPA" },
    visaNeeded: { label: "Visa sponsorship needed", kind: "yesno" },
};
export const candidateFieldSetSchema = z.array(z.enum(CANDIDATE_FIELD_KEYS)).max(CANDIDATE_FIELD_KEYS.length);
/** One set of values. Every key optional; `null` clears a value the recruiter set earlier. CTC is
 *  free text on purpose: currency and convention (LPA, annual, monthly) vary by team and nothing
 *  computes on it. */
export const candidateDetailsSchema = z
    .object({
    noticePeriodDays: z.number().int().min(0).max(365).nullable().optional(),
    currentCtc: z.string().trim().max(60).nullable().optional(),
    expectedCtc: z.string().trim().max(60).nullable().optional(),
    visaNeeded: z.boolean().nullable().optional(),
})
    .strict();
/** The fields in force for a job: its own override when it has one, else the org default. */
export function resolveCandidateFields(orgDefault, jobOverride) {
    const src = Array.isArray(jobOverride) ? jobOverride : (orgDefault ?? []);
    // Keep canonical order and drop anything unknown (an old/foreign key must not render a blank row).
    return CANDIDATE_FIELD_KEYS.filter((k) => src.includes(k));
}
/** Keep only the values for fields that are switched on — a field a team did not ask for is
 *  never stored, even if a client sends it. */
export function pickEnabledDetails(values, enabled) {
    const out = {};
    for (const k of enabled)
        if (values[k] !== undefined)
            out[k] = values[k];
    return out;
}
//# sourceMappingURL=candidateFields.js.map